import React, {
  Fragment,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  Button,
  Grid,
  Typography,
  useTheme,
  Select,
  Input,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Container,
  Modal,
  InputLabel,
  Autocomplete,
  TextField,
  debounce,
  IconButton,
  CircularProgress,
  Collapse,
  Divider,
  Switch,
  Stack,
  FormControlLabel,
  Tooltip,
  FormLabel,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { useLocation, Link } from "react-router-dom";

import writeXlsxFile from "write-excel-file";

import { backend, API_URL } from "../../../../config";

import { useSelector } from "react-redux";
import PickListDetailsRow from "../PickListDetailsRow/PickListDetailsRow";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import LockIcon from "@mui/icons-material/Lock";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  container: {
    maxHeight: 440,
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 440,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  customTable: {
    "& .MuiTableCell-sizeSmall": {
      padding: "6px 0px 6px 8px",
    },
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const columnsHeader = [
  { id: "warehouse", label: "Warehouse", type: "text" },
  { id: "bin", label: "Bin Location", type: "text" },
  { id: "upc", label: "UPC", type: "text" },
  { id: "name", label: "Name", type: "text" },
  { id: "unit_price", label: "Unit Price", type: "number" },
  { id: "case_price", label: "Case Price", type: "number" },
  { id: "unit_type", label: "Unit Type", type: "text" },
  { id: "units_per_case", label: "Units per Case", type: "number" },
  { id: "weight_type", label: "Weight Type", type: "text" },
  { id: "average_unit_weight", label: "Average Unit Weight", type: "number" },
  { id: "average_case_weight", label: "Average Case Weight", type: "number" },
  { id: "stock_level", label: "Stock Level", type: "number" },
  { id: "tax_default", label: "Tax Default", type: "checkbox" },
  { id: "unit_quantity", label: "Units Picked", type: "number" },
  { id: "units_ordered", label: "Unit Ordered", type: "number" },
  { id: "case_quantity", label: "Cases Picked", type: "number" },
  { id: "cases_ordered", label: "Case Ordered", type: "number" },
  { id: "unit_price", label: "[READONLY] Unit Price", type: "number" },
  { id: "case_price", label: "[READONLY] Case Price", type: "number" },
];

const columnsPickList = [
  {
    id: "name",
    label: "Item Name",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "select",
  },
  {
    id: "order",
    label: "Order",
    minWidth: 50,
    align: "center",
  },
  {
    id: "upc",
    label: "UPC",
    minWidth: 50,
    align: "center",
  },
  {
    id: "picked",
    label: "Picked",
    minWidth: 20,
    align: "center",
    exposed: true,
    exposedAs: "checkbox",
  },
  {
    id: "scanned",
    label: "Scanned",
    minWidth: 20,
    align: "center",
  },
  {
    id: "unit_quantity",
    label: "Units Picked",
    minWidth: 20,
    align: "center",
    exposed: true,
    exposedAs: "input",
  },
  {
    id: "units_ordered",
    label: "Unit Ordered",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "input",
  },
  {
    id: "case_quantity",
    label: "Cases Picked",
    minWidth: 20,
    align: "center",
    exposed: true,
    exposedAs: "input",
  },
  {
    id: "cases_ordered",
    label: "Case Ordered",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "input",
  },
  {
    id: "bin",
    label: "Bin",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "input",
  },
  {
    id: "item_codee",
    label: "Item Code",
    minWidth: 50,
    align: "center",
  },
  {
    id: "combined_stock_level",
    label: "Combined Stock",
    minWidth: 20,
    align: "center",
  },
  {
    id: "price_per_unit",
    label: "Unit Price",
    minWidth: 50,
    align: "center",
  },
  {
    id: "price_per_case",
    label: "Case Price",
    minWidth: 50,
    align: "center",
  },
  {
    id: "final_price",
    label: "Subtotal",
    minWidth: 50,
    align: "center",
  },
  {
    id: "catch_weight",
    label: "Weight / Lot",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "select",
  },
  {
    id: "fees",
    label: "Fees",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "select",
  },
  {
    id: "ignore_missing_items",
    label: "Ignore Qty Diff",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "checkbox",
  },
  {
    id: "taxable",
    label: "Tax",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "checkbox",
  },
  {
    id: "qb_mapping",
    label: "QB Mapping",
    minWidth: 70,
    align: "center",
  },
  /* {
    id: "coupon_amount",
    label: "Coupon Amount",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "input",
  },
  {
    id: "coupon_note",
    label: "Coupon Note",
    minWidth: 50,
    align: "center",
    exposed: true,
  }, */
  {
    id: "out_of_stock",
    label: "Out of Stock",
    minWidth: 20,
    align: "center",
    exposed: true,
    exposedAs: "checkbox",
  },
  {
    id: "stock_level",
    label: "Inventory Stock",
    minWidth: 20,
    align: "center",
  },
  {
    id: "to_delete",
    label: "",
    minWidth: 50,
    align: "center",
    exposed: true,
    exposedAs: "button",
  },
];

const columnsPicker = [
  {
    id: "name",
    label: "Item Name",
    minWidth: 50,
    align: "center",
  },
  {
    id: "order",
    label: "Order",
    minWidth: 50,
    align: "center",
  },
  {
    id: "picked",
    label: "Picked",
    minWidth: 20,
    align: "center",
  },
  {
    id: "scanned",
    label: "Scanned",
    minWidth: 20,
    align: "center",
  },
  {
    id: "bin",
    label: "Bin",
    minWidth: 20,
    align: "center",
  },
  {
    id: "item_codee",
    label: "Item Code",
    minWidth: 50,
    align: "center",
  },
  {
    id: "units_ordered",
    label: "Unit Ordered",
    minWidth: 50,
    align: "center",
  },
  {
    id: "cases_ordered",
    label: "Case Ordered",
    minWidth: 50,
    align: "center",
  },
  {
    id: "stock_level",
    label: "Inventory Stock",
    minWidth: 20,
    align: "center",
  },
  {
    id: "combined_stock_level",
    label: "Combined Stock",
    minWidth: 20,
    align: "center",
  },
];

const orderItemProperties = {
  admin_added: false,
  cases_ordered: 0,
  case_quantity: 0,
  coupon_amount: 0,
  coupon_note: "",
  estimated_price: 0,
  price_per_unit: null,
  price_per_case: null,
  final_price: null,
  id: null,
  order: null,
  original_product: null,
  price_snapshot: null,
  product: null,
  taxable: false,
  units_ordered: 0,
  unit_quantity: 0,
};

const PickListDetails = (props) => {
  const classes = useStyles();
  const location = useLocation();
  const theme = useTheme();

  const currentUserStore = useSelector((state) => state.current_user);

  // todo: sortingState prop is being sent but not used atm
  const {
    filterState,
    bulkState,
    dateRangeState,
    ordersState,
    sortingState,
    multiple,
  } = location.state;

  const [filter, setFilter] = useState(filterState);
  const [filterChange, setFilterChange] = useState(false);
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columns, setColumns] = useState(columnsPickList);
  const [orderItems, setOrderItems] = useState([]);
  const [orderItemsLoaded, setOrderItemsLoaded] = useState(false);
  const [orderItemsMapped, setOrderItemsMapped] = useState(false);
  const [orderSubItems, setOrderSubItems] = useState([]);
  const [allPicked, setAllPicked] = useState(false);
  const [pickerView, setPickerView] = useState(false);
  const [hidePicked, setHidePicked] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersMap, setOrdersMap] = useState(null);
  const [ordersGroup, setOrdersGroup] = useState(null);
  // const [productsMapping, setProductsMapping] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [responseModal, setResponseModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [productModal, setProductModal] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [checkingSubCompatibility, setCheckingSubCompatibility] =
    useState(false);
  const [substitutionSettings, setSubstitutionSettings] = useState({
    rowId: null,
    original_product: null,
    product_id: "",
    order_id: "",
    unit_quantity: 0,
    units_ordered: 0,
    case_quantity: 0,
    cases_ordered: 0,
    acceptable_substitution: false,
    save_as_sub: false,
  });
  const [substitutionProduct, setSubstitutionProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsOptions, setProductsOptions] = useState([]);
  const [marginTemplates, setMarginTemplates] = useState([]);
  const [marginTemplatesLoaded, setMarginTemplatesLoaded] = useState(false);
  const [productsQuery, setProductsQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [orderSubItemsLoaded, setOrderSubItemsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingModal, setSavingModal] = useState(false);
  const [cRedirect, setCRedirect] = useState({ redirect: false, to: 0 });
  const [displayDetails, setDisplayDetails] = useState(false);
  const [exportingModal, setExportingModal] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [unlockOrderModal, setUnlockOrderModal] = useState(false);
  const [locked, setLocked] = useState(false);
  const [orderObj, setOrderObj] = useState(null);
  const [itemAddSub, setItemAddSub] = useState(false);
  const [displayOriginalProduct, setDisplayOriginalProduct] = useState(false);

  // todo: change to global context
  const [hideAdditionals, setHideAdditionals] = useState(false);

  const toggleDisplayDetails = () => setDisplayDetails(!displayDetails);

  const handleFilterChange = (e) => {
    const { value } = e.target;
    setFilter(value);
    setFilterChange(true);
  };

  const handlePickerView = () => {
    setPickerView(!pickerView);
  };

  const handleHidePicked = () => {
    setHidePicked(!hidePicked);
  };

  const handleHideAdditionals = () => {
    setHideAdditionals(!hideAdditionals);
  };

  const handleAddNewItem = () => {
    if (orders.length === 1) {
      setSubstitutionSettings((prevState) => ({
        ...prevState,
        order_id: orders[0],
      }));
    }
    toggleProductModal();
  };

  const handleChange = (e) => {
    /**
     *
     * Important note:
     * - units/case_quantity changed to units/cases_ordered
     * - units/case_quantity changed to unit/case_quantity
     * - picked, marked an item as picked just marks it not affecting any quantity field
     *
     */
    const { checked, name, value } = e.target;
    const [columnId, rowId] = name.split("-");

    if (value.includes("_reset_overridden")) {
      setOrderItems((prevState) =>
        prevState.map((item, index) => {
          if (+rowId === index) {
            if (columnId === "bin") {
              item.bin_override = "";
            } else {
              item[columnId] = columnId.includes("unit")
                ? item.units_originally_ordered
                : item.cases_originally_ordered;
            }

            item["overriden"] = false;

            item["modified"] = true;
          }

          return item;
        }),
      );
    } else if (value.includes("_out_of_stock_")) {
      setOrderItems((prevState) =>
        prevState.map((item, index) => {
          if (+rowId === index) {
            item["out_of_stock"] = !item["out_of_stock"];
            item.picked = false;
            item.unit_quantity = 0;
            item.case_quantity = 0;
            item["modified"] = true;
          }

          return item;
        }),
      );
    } else if (value.includes("_custom_substitution_")) {
      setSubstitutionSettings((prevState) => ({
        ...prevState,
        rowId: rowId,
        order_id: orderItems[rowId].order,
        original_product: orderItems[rowId],
      }));
      toggleProductModal();
    } else if (value.includes("_acceptable_substitution_")) {
      const asUPC = +value.replace("_acceptable_substitution_", "");

      setSubstitutionSettings((prevState) => ({
        ...prevState,
        rowId: rowId,
        order_id: orderItems[rowId].order,
        original_product: orderItems[rowId],
        product_id: asUPC,
        acceptable_substitution: true,
      }));
    } else if (value.includes("_original_product_")) {
      const opUPC = +value.replace("_original_product_", "");

      setSubstitutionSettings((prevState) => ({
        ...prevState,
        rowId: rowId,
        order_id: orderItems[rowId].order,
        original_product: orderItems[rowId],
        product_id: opUPC,
        acceptable_substitution: true,
      }));
    } else if (columnId === "allPicked") {
      setAllPicked(checked);
      setOrderItems((prevState) =>
        prevState.map((item) => {
          if (item.out_of_stock) return item;

          item.picked = checked;
          // note: matching units is no longer needed
          // item.unit_quantity = checked ? item.units_ordered : 0;
          // item.case_quantity = checked ? item.cases_ordered : 0;
          item.modified = true;

          return item;
        }),
      );
    } else {
      const columnData = columns.find((item) => item.id === columnId);
      if (columnData.exposed || columnId === "order") {
        setOrderItems((prevState) =>
          prevState.map((item, index) => {
            if (+rowId === index) {
              const columnValue = columnData.hasOwnProperty("exposedAs")
                ? columnData.exposedAs === "checkbox"
                  ? checked
                  : columnId === "to_delete"
                    ? !item[columnId]
                    : value
                : value;
              if (!["order", "bin"].includes(columnId)) {
                item[columnId] = columnValue;
              }

              if (columnId === "bin") {
                item.bin_override = columnValue;
              }
              if (
                columnId === "unit_quantity" ||
                columnId === "case_quantity"
              ) {
                const estimated_price =
                  item.unit_quantity * item.price_per_unit +
                  item.case_quantity * item.price_per_case;
                item["estimated_price"] = estimated_price.toFixed(2);
              }
              // todo: check if they still want to mark as picked if the ordered and quantity match
              /* if (
                columnId === "units_ordered" ||
                columnId === "cases_ordered" ||
                columnId === "unit_quantity" ||
                columnId === "case_quantity"
              ) {
                item["picked"] =
                  +item.unit_quantity === +item.units_ordered &&
                  +item.case_quantity === +item.cases_ordered;
              } */

              // todo: check bulk state logic before pushing new unit fields
              if (bulkState) {
                let dupItemsMap = item.dupitems;
                if (
                  columnId === "unit_quantity" ||
                  columnId === "case_quantity"
                ) {
                  let pickedQty = columnValue;
                  dupItemsMap = item.dupitems.map((dItem) => {
                    if (columnId === "unit_quantity") {
                      dItem.unit_quantity =
                        pickedQty >= dItem.units_ordered
                          ? dItem.units_ordered
                          : pickedQty;
                      pickedQty = pickedQty - dItem.units_ordered;
                    } else {
                      dItem.case_quantity =
                        pickedQty >= dItem.cases_ordered
                          ? dItem.cases_ordered
                          : pickedQty;
                      pickedQty = pickedQty - dItem.cases_ordered;
                    }

                    // todo: check if they still want to mark as picked if the ordered and quantity match
                    /* dItem["picked"] =
                      +dItem.unit_quantity === +dItem.units_ordered &&
                      +dItem.case_quantity === +dItem.cases_ordered; */

                    return dItem;
                  });
                }

                if (columnId === "picked") {
                  dupItemsMap = item.dupitems.map((dItem) => ({
                    ...dItem,
                    picked: columnValue,
                  }));
                }

                if (columnId === "tax") {
                  dupItemsMap = item.dupitems.map((dItem) => ({
                    ...dItem,
                    taxable: columnValue,
                  }));
                }

                if (columnId === "order") {
                  dupItemsMap = item.dupitems.map((dItem) => ({
                    ...dItem,
                    hidden: !dItem.hidden,
                  }));
                }

                item.overriden =
                  (item.units_originally_ordered &&
                    item.units_originally_ordered !== null &&
                    item.units_originally_ordered !== item.units_ordered) ||
                  (item.cases_originally_ordered &&
                    item.cases_originally_ordered !== null &&
                    item.cases_originally_ordered !== item.cases_ordered);

                item.dupitems = dupItemsMap;
              }

              item["modified"] = true;

              return item;
            } else {
              return item;
            }
          }),
        );
      }
    }
  };

  const handleCancel = () => {
    if (props.location.hasOwnProperty("search")) {
      const orderId = !orders.length ? null : orders[0];
      if (props.location.search.includes("order") && orderId) {
        props.history.push(`/order/${orderId}`);
      } else {
        props.history.push("/orders/picklist");
      }
    } else {
      props.history.push("/orders/picklist");
    }
  };

  // 0 = none | 1 = order | 2 = invoice | 3 = orders
  const sendRequest = useCallback(
    async (silence = false, redirect = false, to = 0) => {
      const itemsToSave = orderItems.filter((item) => {
        if (
          (item.to_delete && !item.deleted) ||
          (item.modified && !item.new && !item.deleted) ||
          (item.id === null && item.new && !item.to_delete && !item.deleted)
        )
          return true;

        return false;
      });

      if (itemsToSave.length) {
        console.log("saving changes");
        setIsSaving(true);

        if (!silence) {
          setConfirmModal(false);
          setSavingModal(true);
        }

        await Promise.all(
          orderItems.map(async (item) => {
            if (item.to_delete && !item.deleted) {
              if (item.id === null) {
                item.deleted = true;
              } else {
                return await backend
                  .delete(`${API_URL}orderitems/${item.id}`)
                  .then(() => {
                    item.to_delete = false;
                    item.deleted = true;

                    return item;
                  })
                  .catch((error) => {
                    console.log(error);
                    setErrorInfo({
                      error: true,
                      message: JSON.stringify(error.response.data),
                    });

                    setResponseModal(true);
                  });
              }
            } else if (item.modified && !item.new && !item.deleted) {
              let oiModifiedArray = [];

              if (bulkState) {
                oiModifiedArray = item.dupitems.length
                  ? item.dupitems.map((dItem) => buildOrderItemObjMod(dItem))
                  : [buildOrderItemObjMod(item)];

                oiModifiedArray.forEach(async (oiModified) => {
                  await backend
                    .patch(`${API_URL}orderitems/${oiModified.id}`, oiModified)
                    .then(() => {
                      item.modified = false;
                      item.new = false;
                    })
                    .catch((error) => {
                      console.log(error);
                      setErrorInfo({
                        error: true,
                        message: JSON.stringify(error.response.data),
                      });

                      setResponseModal(true);
                    });
                });
              } else {
                const oiModifiedObj = buildOrderItemObjMod(item);

                return await backend
                  .patch(
                    `${API_URL}orderitems/${oiModifiedObj.id}`,
                    oiModifiedObj,
                  )
                  .then(() => {
                    item.modified = false;
                    item.new = false;

                    return item;
                  })
                  .catch((error) => {
                    console.log(error);
                    setErrorInfo({
                      error: true,
                      message: JSON.stringify(error.response.data),
                    });

                    setResponseModal(true);
                  });
              }
            } else if (
              item.id === null &&
              item.new &&
              !item.to_delete &&
              !item.deleted
            ) {
              if (
                item.unit_quantity > 0 ||
                item.units_ordered > 0 ||
                item.case_quantity > 0 ||
                item.cases_ordered > 0
              ) {
                let oiNewArray = [];
                if (bulkState) {
                  oiNewArray = item.dupitems.map((dItem) =>
                    buildOrderItemObjNew(dItem),
                  );

                  oiNewArray.forEach(async (oiNew) => {
                    await backend
                      .post(`${API_URL}orderitems/`, oiNew)
                      .then(({ data }) => {
                        item = {
                          ...item,
                          ...data,
                          modified: false,
                          new: false,
                        };
                      })
                      .catch((error) => {
                        console.log(error);
                        setErrorInfo({
                          error: true,
                          message: JSON.stringify(error.response.data),
                        });
                        setResponseModal(true);
                      });
                  });
                } else {
                  const oiNewObj = buildOrderItemObjNew(item);

                  return await backend
                    .post(`${API_URL}orderitems/`, oiNewObj)
                    .then(({ data }) => {
                      const itemObj = {
                        ...item,
                        ...data,
                        modified: false,
                        new: false,
                      };

                      return itemObj;
                    })
                    .catch((error) => {
                      console.log(error);
                      setErrorInfo({
                        error: true,
                        message: JSON.stringify(error.response.data),
                      });
                      setResponseModal(true);
                    });
                }
              } else {
                return Promise.resolve(item);
              }
            }

            return Promise.resolve(item);
          }),
        ).then((result) => {
          setOrderItems(result);
          setIsSaving(false);

          if (!silence) {
            setSavingModal(false);
            setResponseModal(true);
          }

          if (redirect) {
            setCRedirect({ redirect: true, to: to });
          }
        });
      } else if (redirect) {
        setCRedirect({ redirect: true, to: to });
      }
    },
    [orderItems, bulkState],
  );

  // const customSubstitution = (row) => (e) => {};
  const customSubstitution = () => {
    // custom substitution
    const productObj = productsOptions.find(
      (item) => item.upc === substitutionSettings.product_id,
    );

    // todo: maybe later on we can display a saving sub message and proceed on success and add the option
    if (substitutionSettings.save_as_sub) {
      saveAsSub();
    }

    setOrderItems((prevState) => {
      if (substitutionSettings.original_product !== null) {
        return prevState.map((item, index) => {
          if (
            item.upc === substitutionSettings.original_product.upc &&
            item.order === substitutionSettings.order_id &&
            index === +substitutionSettings.rowId
          ) {
            const unit_quantity =
              productObj.unit_type === "Unit" ||
              productObj.unit_type === "Unit / Case"
                ? substitutionSettings.original_product.unit_quantity
                : 0;
            const case_quantity =
              productObj.unit_type === "Case" ||
              productObj.unit_type === "Unit / Case"
                ? substitutionSettings.original_product.case_quantity
                : 0;

            const estimated_price =
              case_quantity * productObj.price_per_unit +
              unit_quantity * productObj.price_per_case;

            return {
              ...item,
              ...substitutionSettings.original_product,
              ...productObj,
              estimated_price: estimated_price.toFixed(2),
              unit_quantity: substitutionSettings.unit_quantity,
              units_ordered: substitutionSettings.units_ordered,
              case_quantity: substitutionSettings.case_quantity,
              cases_ordered: substitutionSettings.cases_ordered,
              product: substitutionSettings.product_id,
              modified: true,
              item_codee: productObj.item_code.item_code || "",
              subitems: orderSubItems.filter(
                (osiItem) => osiItem.order_item === item.id,
              ),
              mapped: true,
              new: false,
            };
          } else {
            return item;
          }
        });
      } else {
        const orderItemObj = {
          ...orderItemProperties,
          ...productObj,
          item_codee: productObj.item_code.item_code || "",
          subitems: orderSubItems.filter(
            (osiItem) => osiItem.order_item === productObj.id,
          ),
          order: substitutionSettings.order_id,
          unit_quantity: substitutionSettings.unit_quantity,
          units_ordered: substitutionSettings.unit_quantity,
          case_quantity: substitutionSettings.case_quantity,
          cases_ordered: substitutionSettings.case_quantity,
          margins: [],
          surcharges: [],
          dupitems: [],
          dupitemsOrders: [],
          new: true,
        };
        return [...prevState, orderItemObj];
      }
    });
    handleCloseProductModal();
  };

  const acceptableSubstitution = async () => {
    // acceptable substitution modal confirm
    let originalProductObj = orderItems.find(
      (item) => item.upc === substitutionSettings.original_product.upc,
    );

    if (!originalProductObj) {
      originalProductObj = products.find(
        (item) => item.upc === substitutionSettings.original_product.upc,
      );
    }

    if (!originalProductObj) {
      await backend
        .get(
          `${API_URL}products/${substitutionSettings.original_product.upc}?substitution_detail=true&include_inactive=true&include_nonprimary=true`,
        )
        .then(({ data }) => {
          originalProductObj = data;
        });
    }

    let productObj = originalProductObj.acceptable_substitutions.find(
      (item) => item.upc === substitutionSettings.product_id,
    );

    if (!productObj.hasOwnProperty("item_code")) {
      productObj = products.find(
        (item) => item.upc === substitutionSettings.product_id,
      );
    }

    if (!productObj) {
      await backend
        .get(
          `${API_URL}products/${substitutionSettings.product_id}?substitution_detail=true&include_inactive=true&include_nonprimary=True`,
        )
        .then(({ data }) => {
          productObj = data;
        });
    }

    if (productObj.hasOwnProperty("item_code")) {
      setOrderItems((prevState) => {
        if (substitutionSettings.original_product !== null) {
          return prevState.map((item, oiIndex) => {
            if (
              item.upc === substitutionSettings.original_product.upc &&
              item.order === substitutionSettings.order_id &&
              oiIndex === +substitutionSettings.rowId
            ) {
              const unit_quantity =
                productObj.unit_type === "Unit" ||
                productObj.unit_type === "Unit / Case"
                  ? substitutionSettings.original_product.unit_quantity
                  : 0;
              const case_quantity =
                productObj.unit_type === "Case" ||
                productObj.unit_type === "Unit / Case"
                  ? substitutionSettings.original_product.case_quantity
                  : 0;

              const estimated_price =
                case_quantity * productObj.price_per_unit +
                unit_quantity * productObj.price_per_case;

              // todo: unit and case quantity should be set to 0 if substituted from a product sold in a different unit type
              // todo: add a way so they can see the original product ordered quantities
              return {
                ...item,
                ...substitutionSettings.original_product,
                ...productObj,
                original_product: substitutionSettings.original_product.upc,
                estimated_price: estimated_price.toFixed(2),
                unit_quantity: substitutionSettings.unit_quantity,
                units_ordered: substitutionSettings.units_ordered,
                case_quantity: substitutionSettings.case_quantity,
                cases_ordered: substitutionSettings.cases_ordered,
                product: substitutionSettings.product_id,
                modified: true,
                item_codee: productObj.item_code.item_code || "",
                subitems: orderSubItems.filter(
                  (osiItem) => osiItem.order_item === item.id,
                ),
                mapped: true,
              };
            } else {
              return item;
            }
          });
        } else {
          const orderItemObj = {
            ...orderItemProperties,
            ...productObj,
            item_codee: productObj.item_code.item_code || "",
            subitems: orderSubItems.filter(
              (osiItem) => osiItem.order_item === productObj.id,
            ),
            order: substitutionSettings.order_id,
            new: true,
          };

          return [...prevState, orderItemObj];
        }
      });
    }
    handleCloseSubModal();
  };

  const saveAsSub = async () => {
    const origProductASMap =
      substitutionSettings.original_product.acceptable_substitutions.map(
        (item) => item.upc,
      );

    const payloadObj = {
      acceptable_substitutions: [
        ...origProductASMap,
        substitutionSettings.product_id,
      ],
    };

    await backend
      .patch(
        `${API_URL}productedit/${substitutionSettings.original_product.upc}`,
        payloadObj,
      )
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
        const errorData = error.response.data;

        setErrorInfo({
          error: true,
          message:
            typeof errorData === "object"
              ? errorData.hasOwnProperty("message")
                ? errorData.message
                : errorData.hasOwnProperty("message")
                  ? errorData.detail
                  : errorData
              : error.message,
        });
      });
  };

  const handleSubstitutionSettings = (e) => {
    const { name, value, checked } = e.target;

    setSubstitutionSettings((prevState) => ({
      ...prevState,
      [name]: name === "save_as_sub" ? checked : value,
    }));
  };

  const handleSubstitutionProduct = (e, value) => {
    let productValue = value === null ? "" : value.upc;

    setSubstitutionProduct(value);
    setSubstitutionSettings((prevState) => {
      const unit_quantity =
        value.unit_type === "Unit" || value.unit_type === "Unit / Case"
          ? substitutionSettings?.original_product?.unit_quantity || 0
          : 0;
      const case_quantity =
        value.unit_type === "Case" || value.unit_type === "Unit / Case"
          ? substitutionSettings?.original_product?.case_quantity || 0
          : 0;

      return {
        ...prevState,
        product_id: productValue,
        unit_quantity: unit_quantity,
        units_ordered: unit_quantity,
        case_quantity: case_quantity,
        cases_ordered: case_quantity,
      };
    });
  };

  const handleSubstitutionProductInput = (e, value) => {
    if (e.type !== "change") return;

    setProductsQuery(value);
  };

  const getProductsOptions = useMemo(
    () =>
      debounce(async (query) => {
        setProductsOptions([]);
        await backend
          .get(
            `${API_URL}products?no_page=1&search=${query}&include_inactive=false`,
          )
          .then(({ data }) => {
            setProductsOptions(data);
            setIsLoading(false);
          });
      }, 300),
    [],
  );

  const handleCloseProductModal = () => {
    setSubstitutionSettings({
      rowId: null,
      original_product: null,
      product_id: "",
      order_id: "",
      unit_quantity: 0,
      units_ordered: 0,
      case_quantity: 0,
      cases_ordered: 0,
      acceptable_substitution: false,
      save_as_sub: false,
    });
    setSubstitutionProduct(null);
    setProductModal(false);
    setItemAddSub(true);
  };

  const handleSubitemsChange = (upc, subitems) => {
    setOrderItems((prevState) =>
      prevState.map((item) => {
        if (item.upc !== upc) return item;

        const totalLbs = subitems.reduce(
          (previous, current) => previous + current.weight_lbs,
          0,
        );

        const price =
          subitems[0].unit_type === "u"
            ? item.price_per_unit
            : item.price_per_case;

        const totalNew = price * totalLbs;

        const dupItemsMap = item.dupitems.map((dItem) => {
          return dItem.subitems.map((siItem) => {
            const subitemObjNew = subitems.find(
              (siItemN) => siItemN.id === siItem.id,
            );

            if (!subitemObjNew) return siItem;

            return { ...siItem, ...subitemObjNew };
          });
        });

        return {
          ...item,
          subitems: subitems,
          final_price: totalNew.toFixed(2),
          dupitems: dupItemsMap,
          modified: true,
        };
      }),
    );
  };

  const handleFeesChange = (upc, margins, surcharges) => {
    setOrderItems((prevState) =>
      prevState.map((item) => {
        if (item.upc !== upc) return item;

        return {
          ...item,
          margins: margins,
          surcharges: surcharges,
        };
      }),
    );
  };

  const handleCloseSubModal = () => {
    setSubstitutionSettings({
      rowId: null,
      original_product: null,
      product_id: "",
      order_id: "",
      unit_quantity: 0,
      units_ordered: 0,
      case_quantity: 0,
      cases_ordered: 0,
      acceptable_substitution: false,
      save_as_sub: false,
    });
    setSubstitutionProduct(null);
    setSubModal(false);
    setCheckingSubCompatibility(false);
    setItemAddSub(true);
  };

  // const toggleSubModal = () => setSubModal(!productModal);

  const subModalContent = (
    <div className={classes.modal}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          The acceptable substitution cannot be sold in the same unit type of
          the previous item, please select the new quantities:
        </Grid>
        {substitutionProduct !== null ? (
          <>
            <Grid item xs={12} sm={3}>
              <InputLabel shrink id="unitQty">
                Units Picked
              </InputLabel>
              <TextField
                name="unit_quantity"
                type="number"
                InputProps={{
                  inputProps: { min: 0 },
                }}
                value={substitutionSettings.unit_quantity}
                onChange={handleSubstitutionSettings}
                disabled={substitutionProduct.unit_type === "Case"}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputLabel shrink id="unitOrdered">
                Unit Ordered
              </InputLabel>
              <TextField
                name="units_ordered"
                type="number"
                InputProps={{
                  inputProps: { min: 0 },
                }}
                value={substitutionSettings.units_ordered}
                onChange={handleSubstitutionSettings}
                disabled={substitutionProduct.unit_type === "Case"}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputLabel shrink id="caseQty">
                Cases Picked
              </InputLabel>
              <TextField
                name="case_quantity"
                type="number"
                InputProps={{
                  inputProps: { min: 0 },
                }}
                value={substitutionSettings.case_quantity}
                onChange={handleSubstitutionSettings}
                disabled={substitutionProduct.unit_type === "Unit"}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputLabel shrink id="caseOrdered">
                Case Ordered
              </InputLabel>
              <TextField
                name="cases_ordered"
                type="number"
                InputProps={{
                  inputProps: { min: 0 },
                }}
                value={substitutionSettings.cases_ordered}
                onChange={handleSubstitutionSettings}
                disabled={substitutionProduct.unit_type === "Unit"}
                size="small"
                variant="outlined"
              />
            </Grid>
          </>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12}>
          <div align="right">
            <Button
              onClick={() => handleCloseSubModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => acceptableSubstitution()}
              className={classes.button}
              disabled={
                +substitutionSettings.units_ordered === 0 &&
                +substitutionSettings.cases_ordered === 0
              }
            >
              Confirm
            </Button>
          </div>
        </Grid>
      </Grid>
    </div>
  );

  const toggleProductModal = () => setProductModal(!productModal);

  const productModalContent = (
    <div className={classes.modal}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={9}>
          <InputLabel shrink id="productLabel">
            Product
          </InputLabel>
          <Autocomplete
            fullWidth
            disablePortal
            size="small"
            name="product_id"
            options={productsOptions}
            getOptionLabel={(option) =>
              `${option.name} | (${option.upc}) #${
                option.item_code.item_code || ""
              }#`
            }
            isOptionEqualToValue={(option, value) => option.upc === value.upc}
            filterOptions={(options) => options}
            renderInput={(params) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                {`${option.name} | (${option.upc}) #${
                  option.item_code.item_code || ""
                }# Stock: ${option.stock_level || 0}`}
              </li>
            )}
            getOptionDisabled={(option) =>
              substitutionSettings.original_product !== null
                ? option.upc === substitutionSettings.original_product.upc
                : false
            }
            loading={isLoading}
            onChange={(e, value) => handleSubstitutionProduct(e, value)}
            onInputChange={handleSubstitutionProductInput}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputLabel shrink id="orderLabel">
            Order
          </InputLabel>
          <Select
            fullWidth
            input={<Input />}
            name="order_id"
            value={substitutionSettings.order_id}
            onChange={handleSubstitutionSettings}
            disabled={substitutionSettings.original_product !== null}
            MenuProps={MenuProps}
          >
            <MenuItem value="">None</MenuItem>
            {orders.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        {substitutionProduct !== null ? (
          <>
            <Grid item xs={12} sm={6}>
              {["Unit", "Unit / Case"].includes(
                substitutionProduct.unit_type,
              ) && (
                <>
                  <InputLabel shrink id="unitQty">
                    Unit Qty
                  </InputLabel>
                  <TextField
                    name="unit_quantity"
                    type="number"
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                    value={substitutionSettings.unit_quantity}
                    onChange={handleSubstitutionSettings}
                    size="small"
                    variant="outlined"
                  />
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {["Case", "Unit / Case"].includes(
                substitutionProduct.unit_type,
              ) && (
                <>
                  <InputLabel shrink id="caseQty">
                    Case Qty
                  </InputLabel>
                  <TextField
                    name="case_quantity"
                    type="number"
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                    value={substitutionSettings.case_quantity}
                    onChange={handleSubstitutionSettings}
                    size="small"
                    variant="outlined"
                  />
                </>
              )}
            </Grid>
            {substitutionSettings.original_product !== null && (
              <>
                <Grid item xs={12} sm={12}>
                  <FormControlLabel
                    value="start"
                    control={
                      <Checkbox
                        name="save_as_sub"
                        value={substitutionSettings.save_as_sub}
                        checked={substitutionSettings.save_as_sub}
                        onChange={handleSubstitutionSettings}
                        color="primary"
                      />
                    }
                    label="Save acceptable substitution for later"
                    labelPlacement="end"
                  />
                </Grid>
                {substitutionProduct.unit_type !== "Unit / Case" &&
                  substitutionSettings.original_product.unit_type !==
                    substitutionProduct.unit_type && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="red">
                        Note: The sub product uses a different unit type please
                        adjust the quantities.
                      </Typography>
                    </Grid>
                  )}
                <Grid item xs={12}>
                  <Divider textAlign="left">
                    <FormLabel
                      component="legend"
                      onClick={() =>
                        setDisplayOriginalProduct(!displayOriginalProduct)
                      }
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      Original Product
                      {displayOriginalProduct ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </FormLabel>
                  </Divider>
                  <Collapse in={displayOriginalProduct}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>UPC</TableCell>
                            <TableCell>U/C</TableCell>
                            <TableCell>Units</TableCell>
                            <TableCell>Cases</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              {substitutionSettings.original_product.name}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              {substitutionSettings.original_product.upc}
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              {
                                substitutionSettings.original_product
                                  .units_per_case
                              }
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              {
                                substitutionSettings.original_product
                                  .unit_quantity
                              }
                            </TableCell>
                            <TableCell sx={{ textAlign: "right" }}>
                              {
                                substitutionSettings.original_product
                                  .case_quantity
                              }
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Grid>
              </>
            )}
          </>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12}>
          <div align="right">
            <Button
              onClick={() => handleCloseProductModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => customSubstitution()}
              className={classes.button}
              disabled={
                substitutionSettings.product_id === null ||
                substitutionSettings.order_id === "" ||
                substitutionProduct === null ||
                (substitutionProduct.unit_type === "Unit" &&
                  +substitutionSettings.unit_quantity === 0) ||
                (substitutionProduct.unit_type === "Case" &&
                  +substitutionSettings.case_quantity === 0) ||
                (substitutionProduct.unit_type === "Unit / Case" &&
                  +substitutionSettings.unit_quantity === 0 &&
                  +substitutionSettings.case_quantity === 0)
              }
            >
              Confirm
            </Button>
          </div>
        </Grid>
      </Grid>
    </div>
  );

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      Are you sure you want to save the PickList changes?
      <br />
      <br />
      <Stack direction="row" spacing={1} justifyContent="right">
        <Button onClick={() => toggleConfirmModal()}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => sendRequest(false, true, 3)}
        >
          Confirm
        </Button>
        {ordersMap && Object.keys(ordersMap).length === 1 ? (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest(false, true, 1)}
            >
              Order
            </Button>
            {/* todo: disabled make sure to add variable indicator if item is ready to be invoiced
            also we could add an invoicingReady state which will allow the invoice button to be clickable or not
            whenever an order item is updated */}
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest(false, true, 2)}
              disabled={orderItems.some((oiItem) =>
                oiItem.lot_tracked &&
                oiItem?.subitems &&
                oiItem?.subitems.length
                  ? oiItem.subitems.some(
                      (siItem) =>
                        !siItem.lot_code ||
                        siItem.lot_code === null ||
                        siItem.lot_code === "",
                    )
                  : false,
              )}
            >
              Invoice
            </Button>
          </>
        ) : (
          <></>
        )}
      </Stack>
    </div>
  );

  const toggleResponseModal = () => setResponseModal(!responseModal);

  const responseModalContent = (
    <div className={classes.modal}>
      {errorInfo.error ? (
        <>
          There was an error saving the PickList changes
          <br />
          <br />
          <span
            style={{
              color: "red",
              wordBreak: "break-word",
              maxHeight: 160,
              overflow: "hidden",
              display: "inline-block",
            }}
          >
            <b>Error:</b>
            <br />
            {errorInfo.message}
          </span>
        </>
      ) : (
        <>Changes to the PickList has been successfully saved</>
      )}
      <div align="right">
        <Button
          variant="contained"
          color="primary"
          onClick={() => toggleResponseModal()}
          className={classes.button}
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const savingModalContent = (
    <div className={classes.modal}>
      <div align="center">
        <CircularProgress />
        <div>
          Saving changes <br />
          This may take some time relative to the number of items
        </div>
      </div>
    </div>
  );

  const toggleExportingModal = () => setExportingModal(!exportingModal);

  const exportingModalContent = (
    <div className={classes.modal}>
      Do you want to export the items from the selected warehouse and orders?
      <div align="right">
        <Button
          onClick={() => toggleExportingModal()}
          className={classes.button}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => sendExportingRequest()}
          className={classes.button}
        >
          Confirm
        </Button>
      </div>
    </div>
  );

  const sendExportingRequest = async () => {
    const orderIdStr = orders.join("&order=");

    await backend
      .get(
        `${API_URL}productedit?no_page=1&include_inactive=true&warehouse=${filter}&order=${orderIdStr}`,
      )
      .then(({ data }) => {
        const orderItemsMap = new Map(
          orderItems.map((orderItem) => [orderItem.upc, orderItem]),
        );

        if (data.length) {
          const columnsHeaderMapped = columnsHeader.map((item) => item.label);
          const itemsMapped = data.map((item) => {
            const orderItemObj = orderItemsMap.get(item.upc);
            console.log(orderItemObj);

            return [
              item.warehouse,
              item.bin,
              item.upc,
              item.name,
              item.unit_price,
              item.case_price,
              item.unit_type,
              item.units_per_case,
              item.weight_type,
              item.average_unit_weight,
              item.average_case_weight,
              item.stock_level,
              item.tax_default,
              orderItemObj ? orderItemObj.unit_quantity : 0,
              orderItemObj ? orderItemObj.units_ordered : 0,
              orderItemObj ? orderItemObj.case_quantity : 0,
              orderItemObj ? orderItemObj.cases_ordered : 0,
              orderItemObj?.unit_quantity > 0 ? item.unit_price : 0,
              orderItemObj?.case_quantity > 0 ? item.case_price : 0,
            ];
          });

          const inventoryDataMerge = [columnsHeaderMapped, ...itemsMapped];

          const inventoryDataExport = inventoryDataMerge.map((row, rowIndex) =>
            row.map((cell, cellIndex) => {
              let cellObj = { value: cell };

              if (rowIndex === 0) return cellObj;

              if (cellIndex === 0) {
                if (cell) {
                  cellObj.value = cellObj.value.id;
                } else {
                  cellObj.value = 0;
                }
              }

              if (cellIndex === 2) {
                cellObj.value = "'" + cellObj.value;
              }

              return cellObj;
            }),
          );

          const cDate = new Date();
          const cDateString = new Date(
            cDate.getTime() - cDate.getTimezoneOffset() * 60000,
          ).toISOString();

          writeXlsxFile(inventoryDataExport, {
            fileName: `warehouse${filter} (${cDateString}).xlsx`,
          });
          // todo: make it more user friendly (display a different modal content when requesting
          //       endpoint and processing file and when its finally exported)
          toggleExportingModal();
        } else {
          setErrorInfo({
            error: true,
            message:
              "Couldn't find any information from the selected warehouse and orders.",
          });
        }
      });
  };

  const toggleUnlockOrderModal = () => setUnlockOrderModal(!unlockOrderModal);

  const dismissUnlockOrderModal = (redirect = false) => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleUnlockOrderModal();
  };

  const unlockOrderModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Unlock the order for editing?
          <br />
          <br />
          This will delete the existing invoice for the order.
          <br />
          <br />
          <Stack spacing={2} direction="row" justifyContent="right">
            <Button onClick={() => dismissUnlockOrderModal()}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => sendUnlockOrderRequest()}
            >
              Continue
            </Button>
          </Stack>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              {`Generating invoice`}
              <br />
              This may take some time relative to the orderitems
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              {`There was an error while generating the invoice`}
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                }}
              >
                <b>Error:</b>
                <br />
                {errorInfo.message}
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => dismissUnlockOrderModal()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The order was unlocked successfully.
          <div align="right">
            <Button
              variant="contained"
              color="success"
              onClick={() => dismissUnlockOrderModal()}
              className={classes.button}
            >
              Continue
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const sendUnlockOrderRequest = async () => {
    setLoadingFlag(false);

    const orderId = orders[0];

    if (orderId !== null) {
      await backend
        .post(`${API_URL}orders/${orderId}/unlock`)
        .then(({ data }) => {
          setLocked(false);
          setFinishedFlag(true);
        })
        .catch((error) => {
          console.log(error);
          setErrorInfo({
            error: true,
            message: JSON.stringify(error.response.data),
          });
        });
    }
  };

  const buildOrderItemObjMod = (item) => {
    return {
      admin_added: item.admin_added,
      bin_override: item.bin_override,
      cases_ordered:
        item.cases_ordered < 0 || isNaN(item.cases_ordered)
          ? 0
          : +item.cases_ordered,
      case_quantity:
        item.case_quantity < 0 || isNaN(item.case_quantity)
          ? 0
          : +item.case_quantity,
      coupon_amount: item.coupon_amount,
      coupon_note: item.coupon_note,
      estimated_price: item.estimated_price,
      final_price: item.final_price,
      id: item.id,
      order: item.order,
      original_product: item.original_product,
      out_of_stock: item.out_of_stock,
      price_snapshot: item.price_snapshot,
      product: item.product,
      taxable: item.taxable,
      units_ordered:
        item.units_ordered < 0 || isNaN(item.units_ordered)
          ? 0
          : +item.units_ordered,
      unit_quantity:
        item.unit_quantity < 0 || isNaN(item.unit_quantity)
          ? 0
          : +item.unit_quantity,
      picked: item.picked,
      ignore_missing_items: item.ignore_missing_items,
    };
  };

  const buildOrderItemObjNew = (item) => {
    return {
      bin_override: item.bin_override,
      cases_ordered: item.cases_ordered,
      case_quantity: item.case_quantity,
      coupon_amount: item.coupon_amount,
      coupon_note: item.coupon_note,
      estimated_price: item.estimated_price,
      order: item.order,
      original_product: item.upc,
      out_of_stock: item.out_of_stock,
      product: item.upc,
      taxable: item.taxable,
      units_ordered: item.units_ordered,
      unit_quantity: item.unit_quantity,
      picked: item.picked,
      ignore_missing_items: item.ignore_missing_items,
    };
  };

  useEffect(() => {
    const resolveRequest = async () => {
      const orderId = !orders.length ? null : orders[0];

      if (orderId && cRedirect.to !== 3) {
        if (cRedirect.to === 2) {
          await backend
            .post(`${API_URL}orders/${orderId}/generateinvoice`)
            .then(({ data }) => {
              props.history.push(`/order/${orderId}/invoice`);
            })
            .catch((error) => {
              console.log(error);
              setErrorInfo({
                error: true,
                message: JSON.stringify(error.response.data),
              });
            });
        } else {
          props.history.push(`/order/${orderId}`);
        }
      } else {
        props.history.push(`/orders`);
      }
    };

    if (cRedirect.redirect) {
      resolveRequest();
      setCRedirect({ redirect: false, to: 0 });
    }
  }, [cRedirect, orders, props]);

  const paginate = (arr, size) => {
    return arr.reduce((acc, val, i) => {
      let idx = Math.floor(i / size);
      let page = acc[idx] || (acc[idx] = []);
      page.push(val);

      return acc;
    }, []);
  };

  // auto-save every X secs
  useEffect(() => {
    const interval = setInterval(() => {
      sendRequest(true);
    }, 15000);

    return () => clearInterval(interval);
  }, [sendRequest]);

  useEffect(() => {
    if (productsQuery.length >= 3) {
      setIsLoading(true);
      getProductsOptions(productsQuery);
    }
  }, [productsQuery, getProductsOptions]);

  useEffect(() => {
    const getOrderSubItems = async () => {
      const orderIdStr = orders.join("&order=");

      await backend
        .get(`${API_URL}ordersubitems?order=${orderIdStr}&no_page=1`)
        .then(({ data }) => {
          if (data.length) {
            setOrderSubItems(data);
          }
          setOrderSubItemsLoaded(true);
        });
    };

    const getMarginTemplates = async () => {
      await backend
        .get(`${API_URL}margintemplates?no_page=1`)
        .then(({ data }) => {
          const dataMap = data.map((item) => ({
            ...item,
            deleted: false,
            modified: false,
            new: false,
          }));
          setMarginTemplates(dataMap);
          setMarginTemplatesLoaded(true);
        });
    };

    const getOrderItems = async () => {
      const orderIdStr = orders.join("&order=");
      await backend
        .get(`${API_URL}orderitems?order=${orderIdStr}&no_page=1`)
        .then(({ data }) => {
          const dataMap = data.map((item) => {
            const productMarginTemplates = marginTemplates.filter(
              (margin) => margin.product === item.product,
            );

            return {
              ...item,
              margins: productMarginTemplates,
              surcharges: [],
              mapped: false,
            };
          });
          setOrderItems(dataMap);
          setOrderItemsLoaded(true);
        });
    };

    if (orders.length && !orderSubItems.length && !orderSubItemsLoaded) {
      getOrderSubItems();
    }
    if (
      orders.length &&
      orderSubItemsLoaded &&
      !marginTemplates.length &&
      !marginTemplatesLoaded
    ) {
      getMarginTemplates();
    }
    if (
      orders.length &&
      !orderItems.length &&
      orderSubItemsLoaded &&
      marginTemplatesLoaded
    ) {
      getOrderItems();
    }
  }, [
    orders,
    orderItems,
    orderSubItems,
    orderSubItemsLoaded,
    marginTemplates,
    marginTemplatesLoaded,
  ]);

  useEffect(() => {
    const getProducts = async () => {
      let orderItemsUPC = [];

      orderItems.forEach((item) => {
        orderItemsUPC.push(item.product);
        orderItemsUPC.push(item.original_product);
      });

      const orderItemsUPCUnique = [...new Set(orderItemsUPC)];

      const orderItemsUPCPag = paginate(orderItemsUPCUnique, 100);

      let productsArray = [];

      Promise.all(
        orderItemsUPCPag.map(async (pag) => {
          const productIdStr = pag.join("&upc=");

          await backend
            .get(
              `${API_URL}products?upc=${productIdStr}&substitution_detail=true&include_inactive=true&include_nonprimary=true&no_page=1`,
            )
            .then(({ data }) => {
              const dataMap = data.map((item) => {
                const itemMap = { ...item, unit_in_cart: 0, case_in_cart: 0 };

                return itemMap;
              });

              productsArray = [...productsArray, ...dataMap];
            });
        }),
      ).then(() => {
        setProducts(productsArray);
      });
    };

    if (orderItems.length && !products.length) {
      getProducts();
    }

    return () => {};
  }, [orderItems, products]);

  useEffect(() => {
    const orderItemsMapping = () => {
      setOrderItems((prevState) => {
        let prevStateNew = [];
        if (bulkState) {
          let addedUPCs = [];
          prevState.forEach((item) => {
            const newIndicator = `${item.product}_${
              item.bin_override !== "" ? item.bin_override : item.bin
            }`;

            if (addedUPCs.includes(newIndicator)) return;

            const productData = products.find(
              (product) => product.upc === item.product,
            );

            const originalProductData = products.find(
              (product) => product.upc === item.original_product,
            );

            const dupItemsArray = prevState.filter(
              (oiItem) =>
                oiItem.product === item.product &&
                oiItem.bin_override === item.bin_override,
            );

            const subItems = orderSubItems
              .filter((osiItem) => osiItem.order_item === item.id)
              .map((osiItem) => ({
                ...osiItem,
                order: item.order,
                order_customer: ordersMap[item.order].customer.username,
                order_created: ordersMap[item.order].created_date,
              }));

            // todo:  remove price_per_uinit and price_per_case initilization
            let productObj = {
              price_per_unit: 0,
              price_per_case: 0,
              ...item,
              ...productData,
              lot_tracked: item.lot_tracked,
              item_codee: productData.item_code.item_code || "",
              coupon_amount: item.coupon_amount || 0,
              original_product_name: originalProductData.name || "",
              subitems: subItems,
              order_customer: ordersMap[item.order].customer.username,
              order_created: ordersMap[item.order].created_date,
              dupitems: [],
              dupitemsOrders: [],
              to_delete: false,
              deleted: false,
              modified: false,
              new: false,
              hidden: false,
              mapped: true,
            };

            if (dupItemsArray.length > 1) {
              let unit_quantitySum = 0,
                case_quantitySum = 0,
                units_orderedSum = 0,
                cases_orderedSum = 0,
                units_originally_orderedSum = 0,
                cases_originally_orderedSum = 0,
                final_priceSum = 0,
                dupitemsOrdersArray = [];

              const dupItemsMap = dupItemsArray.map((dItem) => {
                unit_quantitySum += dItem.unit_quantity || 0;
                case_quantitySum += dItem.case_quantity || 0;
                units_orderedSum += dItem.units_ordered || 0;
                cases_orderedSum += dItem.cases_ordered || 0;
                units_originally_orderedSum +=
                  dItem.units_originally_ordered || 0;
                cases_originally_orderedSum +=
                  dItem.cases_originally_ordered || 0;
                final_priceSum += dItem.final_price || 0;
                dupitemsOrdersArray.push(dItem.order);

                const dupItemsSubItemArray = orderSubItems
                  .filter((osiItem) => osiItem.order_item === dItem.id)
                  .map((osiItem) => ({
                    ...osiItem,
                    order: dItem.order,
                    order_customer: ordersMap[dItem.order].customer.username,
                    order_created: ordersMap[dItem.order].created_date,
                  }));

                return {
                  ...productData,
                  ...dItem,
                  item_codee: productData.item_code.item_code || "",
                  coupon_amount: dItem.coupon_amount || 0,
                  subitems: dupItemsSubItemArray,
                  order_customer: ordersMap[dItem.order].customer.username,
                  order_created: ordersMap[dItem.order].created_date,
                  to_delete: false,
                  deleted: false,
                  modified: false,
                  new: false,
                  hidden: true,
                  mapped: true,
                };
              });

              productObj = {
                ...productObj,
                unit_quantity: unit_quantitySum,
                case_quantity: case_quantitySum,
                units_ordered: units_orderedSum,
                cases_ordered: cases_orderedSum,
                overriden:
                  units_originally_orderedSum !== units_orderedSum ||
                  cases_originally_orderedSum !== cases_orderedSum,
                final_price: final_priceSum.toFixed(2),
                dupitems: dupItemsMap,
                dupitemsOrders: [...new Set(dupitemsOrdersArray)],
              };
            }

            addedUPCs.push(newIndicator);

            prevStateNew.push(productObj);
          });
        } else {
          prevStateNew = prevState.map((item) => {
            const productData = products.find(
              (product) => product.upc === item.product,
            );
            const originalProductData = products.find(
              (product) => product.upc === item.original_product,
            );

            // todo:  remove price_per_uinit and price_per_case initilization
            let productObj = {
              price_per_unit: 0,
              price_per_case: 0,
              ...item,
              ...productData,
              lot_tracked: item.lot_tracked,
              item_codee: productData.item_code.item_code || "",
              original_product_name: originalProductData.name || "",
              coupon_amount: item.coupon_amount || 0,
              subitems: orderSubItems.filter(
                (osiItem) => osiItem.order_item === item.id,
              ),
              order_customer: ordersMap[item.order].customer.username,
              order_created: ordersMap[item.order].created_date,
              dupitems: [],
              dupitemsOrders: [],
              to_delete: false,
              deleted: false,
              modified: false,
              new: false,
              mapped: true,
              overriden:
                (item.units_originally_ordered &&
                  item.units_originally_ordered !== null &&
                  item.units_originally_ordered !== item.units_ordered) ||
                (item.cases_originally_ordered &&
                  item.cases_originally_ordered !== null &&
                  item.cases_originally_ordered !== item.cases_ordered),
            };

            return productObj;
          });
        }

        prevStateNew
          .sort((a, b) => {
            if (sortingState === "bin") {
              if (isNaN(+a.bin) && isNaN(+b.bin)) {
                if (a.bin > b.bin) {
                  return 1;
                }
                if (a.bin < b.bin) {
                  return -1;
                }
                return 0;
              }

              if (isNaN(+a.bin)) return 1;
              if (isNaN(+b.bin)) return -1;

              if (+a.bin > +b.bin) {
                return 1;
              }
              if (+a.bin < +b.bin) {
                return -1;
              }
            } else {
              if (isNaN(+a.order) && isNaN(+b.order)) {
                if (a.order > b.order) {
                  return 1;
                }
                if (a.order < b.order) {
                  return -1;
                }
                return 0;
              }

              if (isNaN(+a.order)) return 1;
              if (isNaN(+b.order)) return -1;

              if (+a.order > +b.order) {
                return 1;
              }
              if (+a.order < +b.order) {
                return -1;
              }
            }

            return 0;
          })
          .sort((a, b) => {
            if (sortingState === "bin") {
              if (a.bin === b.bin) {
                return a.name.localeCompare(b.name);
              }

              return 0;
            } else {
              if (a.order === b.order) {
                return a.name.localeCompare(b.name);
              }

              return 0;
            }
          });

        return prevStateNew;
      });
    };

    const areOrderItemsMapped = orderItems.every((item) => item.mapped);

    if (
      orderItems.length &&
      products.length &&
      ordersMap &&
      !orderItemsMapped &&
      !areOrderItemsMapped
    ) {
      orderItemsMapping();
    }
  }, [
    orderItems,
    orderItemsMapped,
    ordersMap,
    orderSubItems,
    products,
    bulkState,
    sortingState,
  ]);

  useEffect(() => {
    if (orderItems.length) {
      const areOrderItemsMapped = orderItems.every((item) => item.mapped);
      if (areOrderItemsMapped) {
        setOrderItemsMapped(true);
      }

      setAllPicked(
        orderItems
          .filter((item) => !item.out_of_stock)
          .every((item) => item.picked),
      );
    }
  }, [orderItems]);

  useEffect(() => {
    if (orderItemsMapped && filter !== "") {
      setFilterChange(true);
    }
  }, [filter, orderItemsMapped]);

  useEffect(() => {
    setColumns(pickerView ? columnsPicker : columnsPickList);
  }, [pickerView]);

  useEffect(() => {
    setOrders(ordersState.map((item) => item.id));
    setOrdersMap(() => {
      let ordersMapObj = {};
      ordersState.forEach((item) => (ordersMapObj[item.id] = item));
      return ordersMapObj;
    });
    setOrdersGroup(() => {
      let ordersGroupObj = {};
      ordersState.forEach((item) =>
        ordersGroupObj.hasOwnProperty(item.customer.username)
          ? ordersGroupObj[item.customer.username].push(item.id)
          : (ordersGroupObj[item.customer.username] = [item.id]),
      );

      return ordersGroupObj;
    });
  }, [ordersState]);

  useEffect(() => {
    const getProduct = async (upc = "") =>
      upc === ""
        ? undefined
        : await backend
            .get(
              `${API_URL}products/${upc}?include_inactive=true&include_nonprimary=true&substitution_detail=true`,
            )
            .then(({ data }) => data);

    const acceptableSubstitution = async (
      originalProductObj = undefined,
      productObj = undefined,
    ) => {
      // acceptable substitution
      if (!originalProductObj) {
        originalProductObj = await getProduct(
          substitutionSettings.original_product.upc,
        );
      }

      if (!productObj) {
        productObj = getProduct(substitutionSettings.product_id);
      }

      if (productObj.hasOwnProperty("item_code")) {
        setOrderItems((prevState) => {
          if (substitutionSettings.original_product !== null) {
            return prevState.map((item, oiIndex) => {
              if (
                item.upc === substitutionSettings.original_product.upc &&
                item.order === substitutionSettings.order_id &&
                oiIndex === +substitutionSettings.rowId
              ) {
                const units_ordered =
                  productObj.unit_type === "Unit" ||
                  productObj.unit_type === "Unit / Case"
                    ? substitutionSettings.original_product.units_ordered
                    : 0;
                const cases_ordered =
                  productObj.unit_type === "Case" ||
                  productObj.unit_type === "Unit / Case"
                    ? substitutionSettings.original_product.cases_ordered
                    : 0;

                const estimated_price =
                  cases_ordered * productObj.price_per_unit +
                  units_ordered * productObj.price_per_case;

                // todo: unit and case quantity should be set to 0 if substituted from a product sold in a different unit type
                // todo: add a way so they can see the original product ordered quantities
                return {
                  ...item,
                  ...substitutionSettings.original_product,
                  ...productObj,
                  estimated_price: estimated_price.toFixed(2),
                  unit_quantity:
                    substitutionSettings.original_product.unit_quantity,
                  units_ordered:
                    substitutionSettings.original_product.units_ordered,
                  case_quantity:
                    substitutionSettings.original_product.case_quantity,
                  cases_ordered:
                    substitutionSettings.original_product.cases_ordered,
                  product: substitutionSettings.product_id,
                  modified: true,
                  item_codee: productObj.item_code.item_code || "",
                  subitems: orderSubItems.filter(
                    (osiItem) => osiItem.order_item === item.id,
                  ),
                  mapped: true,
                };
              } else {
                return item;
              }
            });
          } else {
            const orderItemObj = {
              ...orderItemProperties,
              ...productObj,
              item_codee: productObj.item_code.item_code || "",
              subitems: orderSubItems.filter(
                (osiItem) => osiItem.order_item === productObj.id,
              ),
              order: substitutionSettings.order_id,
              new: true,
            };

            return [...prevState, orderItemObj];
          }
        });
      }
      handleCloseSubModal();
    };

    const checkSubCompatibility = async () => {
      setCheckingSubCompatibility(true);

      let originalProductObj = orderItems.find(
        (item, index) =>
          index === +substitutionSettings.rowId &&
          item.upc === substitutionSettings.original_product.upc,
      );

      if (!originalProductObj) {
        originalProductObj = products.find(
          (item) => item.upc === substitutionSettings.original_product.upc,
        );
      }

      if (!originalProductObj) {
        originalProductObj = await getProduct(
          substitutionSettings.original_product.upc,
        );
      }

      let productObj = originalProductObj.acceptable_substitutions.find(
        (item) => item.upc === substitutionSettings.product_id,
      );

      if (!productObj || !productObj.hasOwnProperty("unit_type")) {
        productObj = await getProduct(substitutionSettings.product_id);
      }

      if (
        productObj.unit_type === "Unit / Case" ||
        (originalProductObj.units_ordered && productObj.unit_type === "Unit") ||
        (originalProductObj.cases_ordered && productObj.unit_type === "Case")
      ) {
        acceptableSubstitution(originalProductObj, productObj);
      } else {
        setSubstitutionProduct(productObj);
        setSubModal(true);
      }
    };

    if (
      substitutionSettings.acceptable_substitution &&
      !checkingSubCompatibility
    ) {
      checkSubCompatibility();
    }
  }, [
    orderItems,
    orderSubItems,
    products,
    substitutionSettings,
    checkingSubCompatibility,
  ]);

  useEffect(() => {
    const filterOrderItems = () => {
      setOrderItems((prevState) =>
        prevState.map((item) => ({
          ...item,
          hidden: filter === "" ? false : item.warehouse.id !== +filter,
        })),
      );
      setFilterChange(false);
    };

    if (orderItems.length && filterChange) {
      filterOrderItems();
    }
  }, [filter, filterChange, orderItems]);

  useEffect(() => {
    const orderGet = async () => {
      const orderId = orders[0];

      await backend.get(`${API_URL}orders/${orderId}`).then(({ data }) => {
        setOrderObj(data);

        if (data.invoiced) {
          setLocked(true);
        }
      });
    };

    if (!multiple && orders.length && orderObj === null) {
      orderGet();
    }
  }, [multiple, orderObj, orders]);

  useEffect(() => {
    if (itemAddSub) {
      setItemAddSub(false);
      sendRequest(true);
    }
  }, [orderItems, itemAddSub, sendRequest]);

  return (
    <>
      <Container>
        <Modal open={subModal && substitutionProduct !== null}>
          {subModalContent}
        </Modal>
        <Modal open={productModal}>{productModalContent}</Modal>
        <Modal open={confirmModal}>{confirmModalContent}</Modal>
        <Modal open={savingModal}>{savingModalContent}</Modal>
        <Modal open={responseModal}>{responseModalContent}</Modal>
        <Modal open={exportingModal}>{exportingModalContent}</Modal>
        <Modal open={unlockOrderModal}>{unlockOrderModalContent}</Modal>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12}>
            <Grid container spacing={3}>
              <Grid item xs={true} sm={true}>
                <Typography variant="h6" component="div">
                  Pick List
                </Typography>
              </Grid>
              <Grid item xs="auto" sm="auto">
                {orderItemsMapped ? (
                  <Link
                    to={{
                      pathname: "/orders/picklist/details/print",
                      search: "?from=picklist",
                      state: {
                        filterState,
                        bulkState,
                        dateRangeState,
                        ordersState,
                        orderItems,
                      },
                    }}
                    style={{ textDecoration: "none" }}
                  >
                    <IconButton
                      style={{ marginLeft: theme.spacing(1) }}
                      variant="contained"
                    >
                      <PrintIcon color="inherit" />
                    </IconButton>
                  </Link>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider textAlign="right">
              <Tooltip title={`${displayDetails ? "Show" : "Hide"} details`}>
                <Switch
                  checked={!displayDetails}
                  onChange={() => toggleDisplayDetails()}
                />
              </Tooltip>
            </Divider>
            <Collapse in={!displayDetails}>
              <Typography variant="subtitle1">
                <strong>Start Date: </strong>
                {dateRangeState[0].toISOString().split("T")[0]}
              </Typography>
              <Typography variant="subtitle1">
                <strong>End Date: </strong>
                {dateRangeState[1].toISOString().split("T")[0]}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Bulk Pull: </strong>
                {"" + bulkState}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Included Orders: </strong>
                {ordersGroup
                  ? Object.keys(ordersGroup).map((item, index) => (
                      <Fragment key={`customer-${index}`}>
                        {`${item} (${ordersGroup[item].join(",")}) `}
                      </Fragment>
                    ))
                  : orders.join(",")}
              </Typography>
            </Collapse>
            <Grid item xs={12} sm={12}>
              <Grid container alignItems="center">
                <Grid item xs={12} sm={true}>
                  <Typography variant="subtitle1">
                    <strong>Filter Locations </strong>
                  </Typography>
                  <Select
                    fullWidth
                    label="Filter Locations"
                    onChange={handleFilterChange}
                    name="filter"
                    input={<Input />}
                    value={filter}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="3">Friendly Foods</MenuItem>
                    <MenuItem value="4">Produce Market</MenuItem>
                    <MenuItem value="2">Restaurant Depot</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <div style={{ display: "flex" }}>
                    {bulkState && filter !== "" ? (
                      <>
                        <Button
                          style={{ marginLeft: theme.spacing(1) }}
                          color="inherit"
                          variant="contained"
                          onClick={() => toggleExportingModal()}
                        >
                          Export
                        </Button>
                        <Link
                          to={`/inventory/bulk`}
                          target="_blank"
                          style={{
                            marginLeft: theme.spacing(1),
                            color: "#000",
                          }}
                        >
                          <Button color="inherit" variant="contained">
                            Import
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <></>
                    )}
                    <Button
                      style={{ marginLeft: theme.spacing(1) }}
                      color={hideAdditionals ? "primary" : "inherit"}
                      variant="contained"
                      onClick={() => handleHideAdditionals()}
                    >
                      ...
                    </Button>
                    <Button
                      style={{ marginLeft: theme.spacing(1) }}
                      color="inherit"
                      variant="contained"
                      onClick={() => handlePickerView()}
                    >
                      {`Display ${pickerView ? "PickList" : "Picker View"}`}
                    </Button>
                    <Button
                      style={{ marginLeft: theme.spacing(1) }}
                      color="inherit"
                      variant="contained"
                      onClick={() => handleHidePicked()}
                    >
                      {`${hidePicked ? "Show" : "Hide"} Picked Items`}
                    </Button>
                    {currentUserStore ? (
                      currentUserStore.role_id !== 14 ? (
                        !locked ? (
                          <Button
                            style={{ marginLeft: theme.spacing(1) }}
                            color="success"
                            variant="contained"
                            onClick={() => handleAddNewItem()}
                          >
                            Add New Item
                          </Button>
                        ) : (
                          <Tooltip
                            title={
                              ["p", "pe", "c", "x"].includes(
                                orderObj?.paid_status,
                              )
                                ? "Order is finalized, cannot be unlocked"
                                : (orderObj.credit_total !== null && orderObj.credit_total !== 0)
                                  ? "Order has credits, cannot be unlocked"
                                  : "Unlock Order for Editing"
                            }
                          >
                            <Button
                              style={{ marginLeft: theme.spacing(1) }}
                              color="error"
                              variant="contained"
                              {...(["p", "pe", "c", "x"].includes(
                                orderObj?.paid_status,
                              ) || (orderObj.credit_total !== null && orderObj.credit_total !== 0)
                                ? undefined
                                : { onClick: () => toggleUnlockOrderModal() })}
                              startIcon={<LockIcon />}
                            >
                              Locked
                            </Button>
                          </Tooltip>
                        )
                      ) : (
                        <></>
                      )
                    ) : (
                      <></>
                    )}
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="sticky table"
                size="small"
                classes={{ root: classes.customTable }}
              >
                <TableHead>
                  <TableRow>
                    {columns
                      .filter((column) =>
                        column.id === "order"
                          ? bulkState
                          : [
                                "price_per_unit",
                                "price_per_case",
                                "fees",
                                "qb_mapping",
                              ].includes(column.id) && hideAdditionals
                            ? false
                            : true,
                      )
                      .map((column) => (
                        <TableCell
                          key={`header-${column.id}`}
                          align={column.align}
                          style={{
                            minWidth: column.minWidth,
                            position: "sticky",
                            left:
                              column.id === "name"
                                ? 0
                                : column.id === "order"
                                  ? 358
                                  : "inherit",
                            background: "white",
                            zIndex: ["name", "order"].includes(column.id)
                              ? 5
                              : 4,
                          }}
                        >
                          {column.label}
                          {column.id === "picked" && !pickerView ? (
                            <>
                              <Checkbox
                                name="allPicked"
                                value={allPicked}
                                checked={allPicked}
                                onChange={handleChange}
                                disabled={locked}
                              />
                            </>
                          ) : (
                            <></>
                          )}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) */}
                  {orderItemsMapped &&
                    orderItems
                      .filter((item) => !item.deleted)
                      .map((row, index) => (
                        <PickListDetailsRow
                          key={`row-${index}`}
                          columns={columns}
                          filterChange={filterChange}
                          hidePicked={hidePicked}
                          hideAdditionals={hideAdditionals}
                          index={index}
                          locked={locked}
                          pickerView={pickerView}
                          row={row}
                          handleChange={handleChange}
                          handleSubitemsChange={handleSubitemsChange}
                          handleFeesChange={handleFeesChange}
                        ></PickListDetailsRow>
                      ))}
                  {!orderItemsMapped ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        Loading items...
                      </TableCell>
                    </TableRow>
                  ) : (
                    <></>
                  )}
                  {orderItemsLoaded && !orderItems.length ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        There are no items in the selected orders
                      </TableCell>
                    </TableRow>
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              {isSaving || !orderItemsMapped ? (
                <CircularProgress />
              ) : (
                <CheckCircleIcon color="success" />
              )}
              <Button
                style={{ marginLeft: theme.spacing(1) }}
                color="error"
                variant="contained"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                style={{ marginLeft: theme.spacing(1) }}
                color="success"
                disabled={!orderItems.length}
                variant="contained"
                onClick={toggleConfirmModal}
              >
                Save
              </Button>
            </div>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default PickListDetails;
